/**
 * モバイル下部タブバー (2026-07-29)。
 *
 * 親指の届く位置に日常導線を置く。最大 5 枠で、5 枠目は「メニュー」＝サイドバー
 * ドロワーを開くトリガー（全機能への入口）。
 *
 * 内部メンバーと外部 SA では見える世界が違うため、タブ構成を分ける。
 * 各タブは権限 (canSee) で出し分け、権限が無い枠は詰めて表示する。
 */
import { Kanban, LayoutDashboard, Menu, Pin, UserRound, Wallet } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { NavLink } from 'react-router-dom';

import { useBoardAttention } from '@/hooks/useBoardAttention';
import { cn } from '@/lib/utils';

export interface MobileNavItem {
  /** 権限判定に使う featureId。null なら常に表示。 */
  featureId: string | null;
  label: string;
  to: string;
  icon: LucideIcon;
  /** NavLink の end 判定（'/' のみ完全一致にする）。 */
  end?: boolean;
  /** 掲示板バッジを出すか。 */
  badge?: 'board';
}

/** 内部メンバー用。 */
export const INTERNAL_NAV: MobileNavItem[] = [
  { featureId: 'dashboard', label: 'ホーム', to: '/', icon: LayoutDashboard, end: true },
  { featureId: 'board.posts', label: '掲示板', to: '/board', icon: Pin, badge: 'board' },
  { featureId: 'sales.deals', label: '商談', to: '/deals', icon: Kanban },
  { featureId: 'sales.contacts', label: '顧客', to: '/customers', icon: UserRound },
];

/** 外部 SA 用（掲示板は外部 SA 非表示のため報酬に差し替え）。 */
export const SA_NAV: MobileNavItem[] = [
  { featureId: 'sa.dashboard', label: 'ホーム', to: '/sa?tab=dashboard', icon: LayoutDashboard },
  { featureId: 'sa.deals', label: '商談', to: '/sa?tab=deals', icon: Kanban },
  { featureId: 'sa.contacts', label: '顧客', to: '/sa?tab=contacts', icon: UserRound },
  { featureId: 'sa.commissions', label: '報酬', to: '/sa?tab=commissions', icon: Wallet },
];

export function MobileBottomNav({
  items,
  onOpenMenu,
}: {
  items: MobileNavItem[];
  onOpenMenu: () => void;
}) {
  const board = useBoardAttention();

  return (
    <nav className="mobile-bottom-nav" aria-label="メインナビゲーション">
      {items.map((item) => {
        const Icon = item.icon;
        const count = item.badge === 'board' ? board.badgeTotal : 0;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) => cn('mobile-nav-item', isActive && 'active')}
          >
            <span className="relative">
              <Icon className="size-5" />
              {count > 0 && (
                <span className="mobile-nav-badge">{count > 99 ? '99+' : count}</span>
              )}
            </span>
            <span>{item.label}</span>
          </NavLink>
        );
      })}
      <button type="button" className="mobile-nav-item" onClick={onOpenMenu}>
        <Menu className="size-5" />
        <span>メニュー</span>
      </button>
    </nav>
  );
}
